import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Lock, Sparkles, Star, Cloud, Settings, Save, ToggleLeft, ToggleRight } from 'lucide-react';

const PrivateAccessGate = ({ onAccessGranted }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [isCheckingSettings, setIsCheckingSettings] = useState(true);
  const [showMiniAdmin, setShowMiniAdmin] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [adminSettings, setAdminSettings] = useState({
    password: '',
    is_enabled: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [adminLoginData, setAdminLoginData] = useState({
    email: '',
    password: ''
  });
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState('');

  useEffect(() => {
    checkPrivateAccessSettings();
  }, []);

  const checkPrivateAccessSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/private-access-settings`);
      if (response.ok) {
        const data = await response.json();
        if (!data.is_enabled) {
          // Private access is disabled, grant access directly
          localStorage.setItem('siteAccess', 'granted');
          localStorage.setItem('accessTimestamp', Date.now().toString());
          onAccessGranted();
          return;
        }
      }
    } catch (error) {
      console.error('Error checking private access settings:', error);
    } finally {
      setIsCheckingSettings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify-private-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Store access in localStorage with session_id
          localStorage.setItem('siteAccess', 'granted');
          localStorage.setItem('accessTimestamp', Date.now().toString());
          localStorage.setItem('sessionId', data.session_id);
          onAccessGranted();
        } else {
          setError('Incorrect password');
          setShake(true);
          setPassword('');
          setTimeout(() => setShake(false), 500);
        }
      } else {
        throw new Error('Failed to verify password');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      setError('Error verifying password. Please try again.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminSettings = async () => {
    try {
      if (!adminToken) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/private-access-settings`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminSettings({
          password: '', // Don't pre-fill for security
          is_enabled: data.is_enabled !== undefined ? data.is_enabled : true
        });
      }
    } catch (error) {
      console.error('Error fetching admin settings:', error);
    }
  };

  const handleAdminLogin = async () => {
    setAdminLoginLoading(true);
    setAdminLoginError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(adminLoginData)
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.token);
        setIsAdminLoggedIn(true);
        setShowAdminLogin(false);
        setShowMiniAdmin(true); // Open mini admin after login
        // Fetch settings after login
        setTimeout(() => fetchAdminSettings(), 100);
      } else {
        setAdminLoginError('Invalid credentials');
      }
    } catch (error) {
      setAdminLoginError('Login failed');
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const handleToggleAdmin = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/private-access-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          is_enabled: !adminSettings.is_enabled
        })
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setAdminSettings(prev => ({
          ...prev,
          is_enabled: updatedSettings.is_enabled
        }));
        
        // If disabled, grant access immediately
        if (!updatedSettings.is_enabled) {
          localStorage.setItem('siteAccess', 'granted');
          localStorage.setItem('accessTimestamp', Date.now().toString());
          localStorage.setItem('sessionId', updatedSettings.session_id);
          onAccessGranted();
        }
      }
    } catch (error) {
      console.error('Error updating admin settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!adminSettings.password.trim()) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/private-access-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          password: adminSettings.password
        })
      });

      if (response.ok) {
        setAdminSettings(prev => ({ ...prev, password: '' })); // Clear field after save
      }
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloudClick = () => {
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount === 5) {
        setShowAdminLogin(true);
        return 0; // Reset counter
      }
      return newCount;
    });
    
    // Reset counter after 3 seconds if not completed
    setTimeout(() => {
      setClickCount(0);
    }, 3000);
  };

  // Show loading while checking settings
  if (isCheckingSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8">
          <div className="flex items-center justify-center space-x-2">
            <motion.div
              className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <span className="text-white">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating clouds */}
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-lg"
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-32 left-1/4 w-40 h-40 bg-purple-400/5 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Glass Morphism Card */}
          <motion.div
            className={`bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 ${
              shake ? 'animate-shake' : ''
            }`}
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                className="inline-block mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto">
                    <Lock className="w-10 h-10 text-white" />
                  </div>
                  <motion.div
                    className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles className="w-4 h-4 text-white" />
                  </motion.div>
                </div>
              </motion.div>

              <motion.h1
                className="text-4xl font-black text-white mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  123zzz
                </span>
              </motion.h1>
              

              
              <motion.p
                className="text-sm text-gray-400 flex items-center justify-center space-x-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <Star className="w-4 h-4 text-yellow-400" />
                <span>Early access required</span>
                <Star className="w-4 h-4 text-yellow-400" />
              </motion.p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Access Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300"
                    placeholder="Enter the exclusive password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </motion.div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-red-200 text-sm text-center"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading || !password}
                className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center space-x-2">
                  {isLoading ? (
                    <>
                      <motion.div
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Access Experience</span>
                    </>
                  )}
                </div>
              </motion.button>
            </form>

            {/* Footer Info */}
            <motion.div
              className="text-center mt-6 pt-6 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              <div className="flex justify-center">
                <motion.button
                  onClick={handleCloudClick}
                  className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 transition-colors group"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Cloud className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>

          {/* Floating particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white/20 rounded-full"
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + i * 10}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.2, 0.8, 0.2],
                }}
                transition={{
                  duration: 3 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.5,
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Mini Admin Dashboard Modal */}
      <AnimatePresence>
        {showAdminLogin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAdminLogin(false)}
            />

            {/* Admin Login Modal */}
            <motion.div
              className="relative z-10 w-full max-w-sm mx-4"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-6">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Admin Access</h3>
                  <p className="text-sm text-gray-300">Private Settings Control</p>
                </div>

                {/* Login Form */}
                <div className="space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="Admin Email"
                      value={adminLoginData.email}
                      onChange={(e) => setAdminLoginData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300"
                      disabled={adminLoginLoading}
                    />
                  </div>
                  <div>
                    <input
                      type="password"
                      placeholder="Admin Password"
                      value={adminLoginData.password}
                      onChange={(e) => setAdminLoginData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300"
                      disabled={adminLoginLoading}
                      onKeyPress={(e) => e.key === 'Enter' ? handleAdminLogin() : null}
                    />
                  </div>

                  {adminLoginError && (
                    <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-red-200 text-sm text-center">
                      {adminLoginError}
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={handleAdminLogin}
                      disabled={adminLoginLoading || !adminLoginData.email || !adminLoginData.password}
                      className="flex-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-3 px-4 rounded-xl disabled:opacity-50"
                    >
                      {adminLoginLoading ? 'Logging in...' : 'Login'}
                    </button>
                    <button
                      onClick={() => setShowAdminLogin(false)}
                      className="px-4 py-3 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {showMiniAdmin && isAdminLoggedIn && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMiniAdmin(false)}
            />

            {/* Settings Modal */}
            <motion.div
              className="relative z-10 w-full max-w-md mx-4"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-6">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Private Access Control</h3>
                  <p className="text-sm text-gray-300">Full Admin Settings</p>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Private Access Mode
                  </label>
                  <div 
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all duration-300 ${
                      adminSettings.is_enabled 
                        ? 'bg-green-500/20 border-green-500/40' 
                        : 'bg-red-500/20 border-red-500/40'
                    }`}
                    onClick={!isSaving ? handleToggleAdmin : undefined}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${adminSettings.is_enabled ? 'bg-green-400' : 'bg-red-400'}`} />
                      <div>
                        <p className="text-white font-medium text-sm">
                          {adminSettings.is_enabled ? 'Enabled' : 'Disabled'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {adminSettings.is_enabled 
                            ? 'Password required' 
                            : 'Direct access'
                          }
                        </p>
                      </div>
                    </div>
                    {isSaving ? (
                      <motion.div
                        className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : adminSettings.is_enabled ? (
                      <ToggleRight className="w-6 h-6 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-red-400" />
                    )}
                  </div>
                </div>

                {/* Password Update */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Change Access Password
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="password"
                      placeholder="New password"
                      value={adminSettings.password}
                      onChange={(e) => setAdminSettings(prev => ({ ...prev, password: e.target.value }))}
                      className="flex-1 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300 text-sm"
                      disabled={isSaving}
                    />
                    <button
                      onClick={handlePasswordUpdate}
                      disabled={isSaving || !adminSettings.password.trim()}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl disabled:opacity-50 hover:shadow-lg transition-all duration-300 text-sm"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to keep current password
                  </p>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setShowMiniAdmin(false)}
                  className="w-full py-2 px-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white font-medium hover:bg-white/20 transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Custom shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default PrivateAccessGate;